// Regras puras da tela de Relatórios (/reports): presets de período, rótulos
// de status/NPS/venda, formatação de duração, colunas de exportação e
// paginação. Sem React, sem API — testável em isolamento.
import { format, subDays, startOfMonth, differenceInMinutes } from "date-fns";
import { i18n } from "../../translate/i18n";

const iso = (d) => format(d, "yyyy-MM-dd");

const t = (k, o) => i18n.t(`aeros.reports.${k}`, o);
// Rótulos como getters: resolvem no idioma atual a cada leitura.
export const PRESETS = ["today", "7d", "30d", "month"].map((key) => ({ key, get label() { return t(`presets.${key}`); } }));

export const presetRange = (key, now = new Date()) => {
  if (key === "today") return { dateFrom: iso(now), dateTo: iso(now) };
  if (key === "7d") return { dateFrom: iso(subDays(now, 6)), dateTo: iso(now) };
  if (key === "30d") return { dateFrom: iso(subDays(now, 29)), dateTo: iso(now) };
  return { dateFrom: iso(startOfMonth(now)), dateTo: iso(now) };
};

export const detectPreset = (dateFrom, dateTo, now = new Date()) =>
  PRESETS.map((p) => p.key).find((k) => {
    const r = presetRange(k, now);
    return r.dateFrom === dateFrom && r.dateTo === dateTo;
  }) || "";

export const DEFAULT_FILTERS = (now = new Date()) => ({
  ...presetRange("month", now),
  searchParam: "",
  queueIds: [],
  userIds: [],
  whatsappIds: [],
  status: [],
  walletId: "",
  onlyRated: false,
});

export const hasActiveFilters = (f) =>
  Boolean(f.searchParam || f.queueIds.length || f.userIds.length || f.whatsappIds.length || f.status.length || f.walletId || f.onlyRated);

// Status vindo do SQL do relatório já é o rótulo em caixa alta.
const st = (key, tone) => ({ key, tone, get label() { return t(`status.${key}`); } });
export const STATUS = {
  ABERTO: st("open", "info"),
  PENDENTE: st("pending", "warning"),
  FECHADO: st("closed", "success"),
  GRUPO: st("group", "purple"),
  NPS: st("nps", "warning"),
  LGPD: st("lgpd", "muted"),
};
export const statusMeta = (s) => STATUS[String(s || "").toUpperCase()] || { key: "", label: s || "—", tone: "muted" };

export const STATUS_PILLS = [
  { key: "", field: "total", get label() { return t("pills.all"); } },
  { key: "open", field: "open", tone: "info", get label() { return t("pills.open"); } },
  { key: "pending", field: "pending", tone: "warning", get label() { return t("pills.pending"); } },
  { key: "closed", field: "closed", tone: "success", get label() { return t("pills.closed"); } },
];

// "0 d, 0 hrs e 39 m" (SQL) → "39min"; "1 d, 2 hrs e 5 m" → "1d 2h"
export const formatDuration = (s) => {
  if (!s || s === "0") return "—";
  const m = String(s).match(/(\d+)\s*d,?\s*(\d+)\s*hr?s?\s*e?\s*(\d+)\s*m/);
  if (!m) return s;
  const [d, h, min] = m.slice(1).map(Number);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${min}min`;
  return `${min}min`;
};

// minutos → "3 min" / "1h 12min" / "2d 3h"
export const formatMinutes = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const min = Math.round(Number(n));
  if (min < 60) return `${min} min`;
  if (min < 1440) return `${Math.floor(min / 60)}h ${min % 60}min`;
  return `${Math.floor(min / 1440)}d ${Math.floor((min % 1440) / 60)}h`;
};

export const npsMeta = (rate) => {
  const r = Number(rate);
  if (!r) return null;
  return { rate: r, tone: r >= 4 ? "success" : r >= 3 ? "warning" : "error" };
};

export const money = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// "Não vendido" só faz sentido em ticket encerrado — em aberto o false é o
// default do banco, não uma decisão do atendente.
export const saleCell = (row) => {
  const t = row;
  if (t?.finalizadoComVenda === true) return { kind: "sold", text: money(t.valorVenda) };
  if (t?.finalizadoComVenda === false && (statusMeta(t.status).key === "closed" || t.motivoNaoVenda)) return { kind: "notSold", text: i18n.t("aeros.reports.notSold", { reason: t.motivoNaoVenda || i18n.t("aeros.reports.noReason") }) };
  return { kind: "none", text: "—" };
};

// "28/08/2026 14:02" → "28/08 14:02"
export const shortDate = (s) => (s ? String(s).replace(/^(\d{2}\/\d{2})\/\d{4}/, "$1") : "");

export const updatedAgo = (at, now = new Date()) => {
  if (!at) return "";
  const min = differenceInMinutes(now, at);
  if (min < 1) return t("updatedNow");
  if (min < 60) return t("updatedMin", { min });
  return t("updatedHour", { h: Math.floor(min / 60) });
};

export const periodLabel = (dateFrom, dateTo) => {
  const br = (s) => (s ? s.split("-").reverse().join("/") : "");
  return `${br(dateFrom).slice(0, 5)} – ${br(dateTo)}`;
};

export const pageRange = (page, size, total) => {
  if (!total) return t("noResults");
  const from = (page - 1) * size + 1;
  return t("showing", { from, to: Math.min(page * size, total), total });
};

// Variação percentual vs período anterior → { text, tone }
export const delta = (cur, prev, { unit = "%", lowerIsBetter = false, suffix } = {}) => {
  if (prev == null || cur == null || !Number(prev)) return null;
  if (suffix === undefined) suffix = t("vsPrevious");
  const diff = Number(cur) - Number(prev);
  if (Math.abs(diff) < 0.005) return { text: t("samePrevious"), tone: "muted" };
  const up = diff > 0;
  const good = lowerIsBetter ? !up : up;
  const val = unit === "%" ? `${Math.round((Math.abs(diff) / Math.abs(Number(prev))) * 100)}%` : `${Math.abs(Math.round(diff))}${unit}`;
  return { text: `${up ? "▲" : "▼"} ${val} ${suffix}`, tone: good ? "success" : "error" };
};

// Colunas exportáveis (chave = campo da linha do relatório).
const col = (key, labelKey, on, width, format) => ({ key, on, width, format, get label() { return t(`columns.${labelKey}`); } });
export const EXPORT_COLUMNS = [
  col("id", "id", true, 8),
  col("whatsappName", "connection", true, 15),
  col("contactName", "contact", true, 25),
  col("contactNumber", "number", false, 16),
  col("userName", "user", true, 20),
  col("queueName", "queue", true, 15),
  col("walletName", "wallet", false, 18),
  col("status", "status", true, 12),
  col("lastMessage", "lastMessage", false, 30),
  col("createdAt", "opened", true, 17),
  col("closedAt", "closed", true, 17),
  col("supportTime", "supportTime", true, 18, formatDuration),
  col("NPS", "nps", true, 8, (v) => (Number(v) ? v : "")),
  col("valorVenda", "saleValue", true, 15, (v) => (v == null ? "-" : v)),
  col("motivoNaoVenda", "noSaleReason", false, 20, (v) => v || "-"),
  col("finalizadoComVenda", "soldLabel", true, 15, (v) => (v === true ? t("yes") : v === false ? t("no") : "-")),
];

export const buildExportRows = (tickets, columns) =>
  (tickets || []).map((t) => columns.map((c) => (c.format ? c.format(t[c.key], t) : t[c.key] ?? "")));

export const TRANSCRIPT_LIMIT = 200;
